import { getCachedProfile } from '../hooks/useNostrQuery';

// ── Blocked NIP-05 domains ───────────────────────────────────────
// Accounts with NIP-05 identifiers from these domains are hidden
// from feeds. These are known bot / spam farms that flood public
// relays with low-quality content tagged #bch.
//
// To add a new domain: just append it to this array.
const BLOCKED_NIP05_DOMAINS: string[] = [
  'poster.place',
];

/** Check if a NIP-05 identifier belongs to a blocked domain */
export function isBlockedNip05(nip05: string | undefined | null): boolean {
  if (!nip05 || typeof nip05 !== 'string') return false;
  const lower = nip05.toLowerCase();
  const domain = lower.split('@')[1];
  if (!domain) return false;
  return BLOCKED_NIP05_DOMAINS.some(blocked => domain === blocked || domain.endsWith('.' + blocked));
}

/**
 * Check if a pubkey belongs to a known spam/bot account based on their
 * cached profile. Uses the in-memory profile cache — zero network cost.
 * Returns false if the profile isn't cached yet (benefit of the doubt).
 */
export function isSpamAccount(pubkey: string): boolean {
  const profile = getCachedProfile(pubkey);
  if (!profile) return false;
  return isBlockedNip05(profile.nip05);
}

// BCH content detection — only show posts relevant to Bitcoin Cash
const BCH_KEYWORDS = [
  'bitcoin cash', 'bitcoincash', 'bch', 'bitcoin-cash',
  'cashtokens', 'cashtoken', 'cashscript',
  'electron cash', 'electroncash',
  'flipstarter', 'cashfusion', 'cashshuffle',
  'bchnostr', 'bch nostr',
  'satoshi', 'p2p cash', 'p2p electronic cash',
  'bchn', 'bchd', 'bitcoin abc',
  'smartbch', 'chipnet',
  'bitcoincash:', // cashaddr
  'simpleledger:', // SLP
];

const BCH_HASHTAGS = [
  'bch', 'bitcoincash', 'bitcoin-cash', 'cashtokens',
  'bchnostr', 'cashfusion', 'electroncash', 'flipstarter',
  'smartbch', 'cashscript', 'bitcoincashnode',
];

export function isBCHContent(content: string, tags?: string[][]): boolean {
  const lower = content.toLowerCase();

  // Check content for BCH keywords
  for (const kw of BCH_KEYWORDS) {
    if (lower.includes(kw)) return true;
  }

  // Check hashtag tags
  if (tags) {
    for (const tag of tags) {
      if (tag[0] === 't' && BCH_HASHTAGS.includes(tag[1]?.toLowerCase())) {
        return true;
      }
    }
  }

  // Check for BCH address patterns
  if (/bitcoincash:q[a-z0-9]{41}/i.test(content)) return true;
  if (/\bq[a-z0-9]{41}\b/.test(content)) return true; // cashaddr without prefix

  return false;
}

// ── Spam filter ───────────────────────────────────────────────────
// Patterns are checked against lowercased content.
// Keep patterns specific enough to avoid false positives on
// legitimate BCH discussion (e.g. "invest in BCH" should NOT be blocked).

const SPAM_PATTERNS: RegExp[] = [
  // ── Scam / financial fraud ────────────────────────────────────
  /t\.me\//i,                               // Telegram links
  /join.*telegram/i,
  /whatsapp.*group/i,
  /free\s*airdrop/i,
  /airdrop.*claim/i,
  /claim.*airdrop/i,
  /send\s*\d+.*get\s*\d+\s*back/i,          // send X get Y back
  /send\s*\d+.*receive\s*\d+/i,
  /double\s*your\s*(money|crypto|bitcoin|btc|eth)/i,
  /multiply\s*your\s*(money|crypto|coins)/i,
  /guaranteed\s*(profit|returns|income|earnings)/i,
  /\d+%\s*(daily|weekly|monthly)\s*(profit|return|roi|interest)/i,
  /passive\s*income\s*of\s*\d+/i,
  /earn\s*\d+\s*(btc|eth|usdt|usd)\s*(daily|weekly|per day)/i,
  /risk[\s-]free\s*investment/i,
  /ponzi/i,
  /pyramid\s*scheme/i,

  // ── Phishing / credential theft ───────────────────────────────
  /click.*link.*bio/i,
  /link\s*in\s*(my\s*)?bio/i,
  /verify.*wallet/i,
  /connect.*wallet.*now/i,
  /wallet.*compromised/i,
  /seed\s*phrase/i,                          // asking for seed phrases
  /private\s*key.*send/i,
  /synchroni[sz]e.*wallet/i,
  /validate.*wallet/i,

  // ── Shilling / pump schemes ───────────────────────────────────
  /buy\s*(now|fast|quick|immediately)\s*(before|or miss)/i,
  /next\s*(100x|1000x|10x|moon|gem)/i,
  /\d+x\s*(guaranteed|profit|return)/i,
  /pumping\s*(soon|hard|now)/i,
  /get\s*(in\s*)?(now|early|quick)\s*(before|or)/i,
  /going\s*to\s*the\s*moon/i,               // "going to the moon" pump talk
  /ape\s*(in|into)\s*now/i,
  /last\s*chance\s*to\s*buy/i,
  /dont\s*miss\s*out/i,

  // ── Self-promotion / follow spam ─────────────────────────────
  /follow\s*(me|us)\s*(back|for|and)/i,
  /follow\s*for\s*follow/i,
  /f4f\b/i,
  /l4l\b/i,                                  // like for like
  /sub4sub/i,
  /i\s*follow\s*back/i,
  /mutual\s*follow/i,

  // ── Adult / explicit content ──────────────────────────────────
  /onlyfans\.com/i,
  /only\s*fans/i,
  /xxx\b/i,
  /nude(s)?\b/i,
  /\bporn\b/i,
  /sex\s*(tape|video|content|chat)/i,
  /escort\s*service/i,
  /\bnsfw\b.*link/i,

  // ── Generic spam signals ──────────────────────────────────────
  /\binvest\s+now\b/i,                       // "invest now" (not "invest in BCH")
  /limited\s*time\s*offer/i,
  /act\s*now\s*(or|before)/i,
  /\b(winner|won|prize)\b.*\bclaim\b/i,      // lottery scams
  /you\s*have\s*been\s*selected/i,
  /congratulations.*won/i,
  /make\s*money\s*(online|fast|from home)/i,
  /work\s*from\s*home.*earn/i,
  /\$\d+\s*(per|a)\s*(day|hour|week)\s*(from home|online|guaranteed)/i,
];

// ── Bot / automated behavior patterns ─────────────────────────────
// These catch common bot signatures that aren't necessarily scams
// but are clearly automated, low-quality, or engagement-farming content.

const BOT_PATTERNS: RegExp[] = [
  // ── Generic AI-generated filler ────────────────────────────────
  /not\s*financial\s*advice\s*but/i,

  // ── Bot signature patterns (full-match only — must be the entire post) ──
  // These only trigger when the ENTIRE post is just these phrases
  /^(great|nice|good|amazing|awesome|excellent|wonderful)\s*(post|content|share|thread|article|work|one)\s*[!.\u{1F44D}\u{1F525}\u{1F4AF}]*$/iu,
  /^(thanks?\s*for\s*sharing|well\s*said|so\s*true)\s*[!.\u{1F44D}\u{1F525}\u{1F4AF}]*$/iu,
  /^interesting\s*[!.]*$/i,

  // ── Crypto airdrop / engagement bots ───────────────────────────
  /retweet.*\bwin\b/i,
  /like.*repost.*\bwin\b/i,
  /tag\s*\d+\s*friends/i,
  /drop\s*your\s*(wallet|address)/i,
  /giveaway.*follow/i,
  /follow.*giveaway/i,
];

// Structural signals — things that look spammy regardless of content
// e.g. posts that are almost entirely hashtags or emojis

/** Returns true if the post looks like spam and should be hidden from the feed */
export function isSpamContent(content: string): boolean {
  // Check regex patterns
  for (const pattern of SPAM_PATTERNS) {
    if (pattern.test(content)) return true;
  }

  // Check bot patterns
  const trimmed = content.trim();
  for (const pattern of BOT_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }

  // Too many hashtags (more than 15) — hashtag stuffing
  // NOTE: Campaign participation posts commonly use 7-10 hashtags
  // (e.g. #bch #bitcoincash #activity #campaign #community #bchnostr etc.)
  // A threshold of 6 was silently filtering out legitimate campaign entries.
  const hashtagCount = (content.match(/#\w+/g) || []).length;
  if (hashtagCount > 15) return true;

  // Post is almost entirely emojis (>70% of non-space chars are emoji)
  // Catches posts like "🚀🚀🚀💰💰💎💎🔥🔥" with no real content
  const noSpaces = content.replace(/\s/g, '');
  if (noSpaces.length > 10) {
    const emojiMatches = noSpaces.match(
      /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}]/gu
    ) || [];
    if (emojiMatches.length / noSpaces.length > 0.7) return true;
  }

  // Extremely short content that is literally just "gm" or "gn" with nothing else
  // (common bot spam pattern — real users usually add something)
  const lowerTrimmed = trimmed.toLowerCase().replace(/[^a-z\s]/g, '').trim();
  if (lowerTrimmed === 'gm' || lowerTrimmed === 'gn') return true;

  return false;
}

/**
 * Detect potential bot-like posting patterns from a batch of events.
 * Returns the number of "bot signals" detected. Higher = more suspicious.
 * This is used for display purposes only — NOT for blocking.
 */
export function detectBotSignals(events: { content: string; created_at: number }[]): number {
  if (events.length < 3) return 0;
  let signals = 0;

  // 1. Duplicate/near-duplicate content — bots repost the same thing
  const contentSet = new Set<string>();
  let duplicates = 0;
  for (const e of events) {
    const normalized = e.content.trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 200);
    if (contentSet.has(normalized)) duplicates++;
    contentSet.add(normalized);
  }
  if (duplicates > events.length * 0.3) signals += 2;
  if (duplicates > events.length * 0.5) signals += 3;

  // 2. Suspiciously regular posting intervals (bots post on schedule)
  if (events.length >= 5) {
    const sorted = [...events].sort((a, b) => a.created_at - b.created_at);
    const gaps: number[] = [];
    for (let i = 1; i < sorted.length; i++) {
      gaps.push(sorted[i].created_at - sorted[i - 1].created_at);
    }
    const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
    const variance = gaps.reduce((a, g) => a + Math.pow(g - avgGap, 2), 0) / gaps.length;
    const stdDev = Math.sqrt(variance);
    // If std deviation is less than 10% of mean, intervals are suspiciously regular
    if (avgGap > 0 && stdDev / avgGap < 0.1 && events.length >= 10) signals += 3;
  }

  // 3. All posts are very short (< 50 chars) — filler content
  const shortPosts = events.filter(e => e.content.trim().length < 50);
  if (shortPosts.length > events.length * 0.8) signals += 1;

  return signals;
}

